import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

const AdminStudentSurveyReport = () => {
  const navigate = useNavigate();

  // Data state
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 300;

  // Filter state
  const [cycleYear, setCycleYear] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [currentCycleYear, setCurrentCycleYear] = useState(null);
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [universities, setUniversities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Sorting state
  const [sortBy, setSortBy] = useState('application_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Export state
  const [exporting, setExporting] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch available cycles on mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const token = getValidToken();
        const response = await axios.get(`${API_URL}/api/admin/student-survey-report/cycles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setAvailableCycles(response.data.cycles);
          setCurrentCycleYear(response.data.current_cycle_year);
          // Set initial cycle year to current
          if (response.data.current_cycle_year) {
            setCycleYear(response.data.current_cycle_year);
          } else if (response.data.cycles.length > 0) {
            setCycleYear(response.data.cycles[0].year);
          }
        }
      } catch (err) {
        console.error('Error fetching cycles:', err);
        setError('Failed to load cycle years');
      }
    };
    fetchCycles();
  }, []);

  // Fetch students data
  const fetchStudents = useCallback(async () => {
    if (!cycleYear) return;

    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedUniversity) {
        params.append('university', selectedUniversity);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/student-survey-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStudents(response.data.students);
        setTotalPages(response.data.pagination.total_pages);
        setTotalRecords(response.data.pagination.total_records);
        setCurrentPage(response.data.pagination.page);
        setUniversities(response.data.filters.universities);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load student data');
    } finally {
      setLoading(false);
    }
  }, [cycleYear, currentPage, sortBy, sortOrder, searchTerm, selectedUniversity]);

  // Fetch students when dependencies change
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return sortOrder === 'asc'
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const token = getValidToken();
      const params = new URLSearchParams({
        cycle_year: cycleYear,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedUniversity) {
        params.append('university', selectedUniversity);
      }

      const response = await axios.get(
        `${API_URL}/api/admin/student-survey-report/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `student_survey_report_cycle${cycleYear}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle cycle year change
  const handleCycleChange = (e) => {
    setCycleYear(parseInt(e.target.value));
    setCurrentPage(1);
    setSelectedUniversity('');
  };

  // Handle university filter change
  const handleUniversityChange = (e) => {
    setSelectedUniversity(e.target.value);
    setCurrentPage(1);
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 my-4">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀◀
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ◀ Prev
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded border ${
              currentPage === page
                ? 'bg-blue-600 text-white'
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next ▶
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ▶▶
        </button>
      </div>
    );
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Survey Status Report</h1>
              <p className="text-sm text-gray-600 mt-1">View and export student application data by recruitment cycle</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border rounded hover:bg-gray-50"
            >
              ← Back to Admin
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Cycle Year Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Year</label>
              <select
                value={cycleYear || ''}
                onChange={handleCycleChange}
                className="px-3 py-2 border rounded-md min-w-[180px] bg-white"
              >
                {availableCycles.map(cycle => (
                  <option key={cycle.year} value={cycle.year}>
                    {cycle.year} {cycle.is_current ? '(Current)' : ''} - {cycle.student_count.toLocaleString()} students
                  </option>
                ))}
              </select>
            </div>

            {/* University Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
              <select
                value={selectedUniversity}
                onChange={handleUniversityChange}
                className="px-3 py-2 border rounded-md min-w-[200px] bg-white"
              >
                <option value="">All Universities</option>
                {universities.map(uni => (
                  <option key={uni} value={uni}>{uni}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name or email..."
                className="px-3 py-2 border rounded-md min-w-[250px]"
              />
            </div>

            {/* Export Button */}
            <div className="ml-auto">
              <button
                onClick={handleExport}
                disabled={exporting || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Exporting...
                  </>
                ) : (
                  <>
                    📥 Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Pagination Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 text-lg">ℹ️</span>
              <span className="text-blue-800 font-medium">
                Displaying {perPage} records per page | Page {currentPage} of {totalPages} | {totalRecords.toLocaleString()} total students
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-blue-600 text-sm font-medium">
                <span className="text-blue-500">*</span> = self-reported data
              </span>
              {cycleYear === currentCycleYear && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Current Cycle
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Top Pagination */}
        {totalPages > 1 && <Pagination />}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Data Table - Fixed height with sticky columns */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-auto max-h-[600px]" style={{ position: 'relative' }}>
            <table className="min-w-full divide-y divide-gray-200 border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-30">
                <tr>
                  {/* Sticky columns: ID, First Name, Last Name, Name */}
                  <th
                    onClick={() => handleSort('applicant_id')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky left-0 z-20"
                    style={{ minWidth: '70px' }}
                  >
                    ID {renderSortIcon('applicant_id')}
                  </th>
                  <th
                    onClick={() => handleSort('first_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky z-20"
                    style={{ left: '70px', minWidth: '100px' }}
                  >
                    First Name {renderSortIcon('first_name')}
                  </th>
                  <th
                    onClick={() => handleSort('last_name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky z-20"
                    style={{ left: '170px', minWidth: '100px' }}
                  >
                    Last Name {renderSortIcon('last_name')}
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 sticky z-20 border-r-2 border-gray-300"
                    style={{ left: '270px', minWidth: '150px' }}
                  >
                    Name {renderSortIcon('name')}
                  </th>
                  {/* Non-sticky columns */}
                  <th
                    onClick={() => handleSort('university_email')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Email {renderSortIcon('university_email')}
                  </th>
                  <th
                    onClick={() => handleSort('current_university')}
                    className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-50"
                  >
                    University<span className="text-blue-500">*</span> {renderSortIcon('current_university')}
                  </th>
                  <th
                    onClick={() => handleSort('current_academic_class')}
                    className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-50"
                  >
                    Class<span className="text-blue-500">*</span> {renderSortIcon('current_academic_class')}
                  </th>
                  <th
                    onClick={() => handleSort('citizenship_status')}
                    className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-50"
                  >
                    Citizenship<span className="text-blue-500">*</span> {renderSortIcon('citizenship_status')}
                  </th>
                  <th
                    onClick={() => handleSort('race_verified')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Race {renderSortIcon('race_verified')}
                  </th>
                  <th
                    onClick={() => handleSort('ethnicity_verified')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Ethnicity {renderSortIcon('ethnicity_verified')}
                  </th>
                  <th
                    onClick={() => handleSort('gender_verified')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Gender {renderSortIcon('gender_verified')}
                  </th>
                  <th
                    onClick={() => handleSort('date_of_birth')}
                    className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-50"
                  >
                    DOB<span className="text-blue-500">*</span> {renderSortIcon('date_of_birth')}
                  </th>
                  <th
                    onClick={() => handleSort('application_date')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    App Date {renderSortIcon('application_date')}
                  </th>
                  {/* Survey Status Columns */}
                  <th className="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50 border-l-2 border-blue-200">
                    Applied
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-yellow-600 uppercase tracking-wider bg-yellow-50">
                    Interviewing
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50">
                    Offered
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider bg-green-50">
                    Accepted
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider bg-orange-50">
                    Declined
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-red-600 uppercase tracking-wider bg-red-50">
                    Rejected
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-100 border-l-2 border-gray-300">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="20" className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="animate-spin text-2xl">⏳</span>
                        Loading students...
                      </div>
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="20" className="px-4 py-8 text-center text-gray-500">
                      No students found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  students.map((student, index) => (
                    <tr key={student.applicant_id || index} className="hover:bg-gray-50 group">
                      {/* Sticky columns with background */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky left-0 z-10" style={{ minWidth: '70px' }}>
                        {student.applicant_id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky z-10" style={{ left: '70px', minWidth: '100px' }}>
                        {student.first_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky z-10" style={{ left: '170px', minWidth: '100px' }}>
                        {student.last_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50 sticky z-10 border-r-2 border-gray-200" style={{ left: '270px', minWidth: '150px' }}>
                        {student.name || '-'}
                      </td>
                      {/* Non-sticky columns */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <a
                          href={`mailto:${student.university_email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {student.university_email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate" title={student.current_university}>
                        {student.current_university || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {student.current_academic_class || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {student.citizenship_status || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {student.race_verified || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {student.ethnicity_verified || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {student.gender_verified || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(student.date_of_birth)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(student.application_date)}
                      </td>
                      {/* Survey Status Columns */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-blue-50 border-l-2 border-blue-200">
                        <span className={`font-medium ${student.applied_count > 0 ? 'text-blue-700' : 'text-gray-400'}`}>
                          {student.applied_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-yellow-50">
                        <span className={`font-medium ${student.interviewing_count > 0 ? 'text-yellow-700' : 'text-gray-400'}`}>
                          {student.interviewing_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-purple-50">
                        <span className={`font-medium ${student.offered_count > 0 ? 'text-purple-700' : 'text-gray-400'}`}>
                          {student.offered_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-green-50">
                        <span className={`font-medium ${student.accepted_count > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                          {student.accepted_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-orange-50">
                        <span className={`font-medium ${student.declined_count > 0 ? 'text-orange-700' : 'text-gray-400'}`}>
                          {student.declined_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-red-50">
                        <span className={`font-medium ${student.rejected_count > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                          {student.rejected_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center bg-gray-100 border-l-2 border-gray-300">
                        <span className={`font-bold ${student.total_responses > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                          {student.total_responses || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Pagination */}
        {totalPages > 1 && <Pagination />}
      </main>
    </div>
  );
};

export default AdminStudentSurveyReport;
